/**
 * Content Block Skeleton Component
 * 
 * Loading skeletons for different content block types.
 * Provides visual feedback while content is being fetched.
 */

import React from 'react';
import { ContentBlockType } from '@/types/template.types';

interface ContentBlockSkeletonProps {
  type: ContentBlockType;
  columns?: number;
}

export const ContentBlockSkeleton: React.FC<ContentBlockSkeletonProps> = ({ 
  type, 
  columns = 3 
}) => {
  const skeletons = {
    news: <NewsBlockSkeleton columns={columns} />,
    video: <VideoBlockSkeleton columns={columns} />,
    categories: <CategoryBlockSkeleton columns={columns} />,
    ads: <AdBlockSkeleton />,
    profiles: <ProfileBlockSkeleton columns={columns} />,
    gallery: <GalleryBlockSkeleton columns={columns} />,
    text: <TextBlockSkeleton />,
    images: <ImagesBlockSkeleton columns={columns} />,
    item_detail: <ItemDetailBlockSkeleton />,
  };

  return skeletons[type] || <GenericBlockSkeleton columns={columns} />;
};

// ============================================================================
// News Block Skeleton
// ============================================================================

const NewsBlockSkeleton: React.FC<{ columns: number }> = ({ columns }) => {
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-6`}>
      {Array.from({ length: columns * 2 }).map((_, i) => (
        <div key={i} className="animate-pulse bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Image placeholder */}
          <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer" />
          
          <div className="p-4 space-y-3">
            {/* Category and date */}
            <div className="flex gap-3">
              <div className="h-5 bg-gray-200 rounded w-16" />
              <div className="h-5 bg-gray-200 rounded w-24" />
            </div>
            
            {/* Title */}
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-full" />
              <div className="h-6 bg-gray-200 rounded w-4/5" />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
            
            {/* Footer */}
            <div className="flex justify-between items-center pt-2">
              <div className="h-4 bg-gray-200 rounded w-20" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Video Block Skeleton
// ============================================================================

const VideoBlockSkeleton: React.FC<{ columns: number }> = ({ columns }) => {
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-6`}>
      {Array.from({ length: columns * 2 }).map((_, i) => (
        <div key={i} className="animate-pulse bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Video thumbnail with play button */}
          <div className="relative h-48 bg-gray-300">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-gray-400 rounded-full" />
            </div>
            <div className="absolute bottom-2 right-2 h-5 w-12 bg-gray-400 rounded" />
          </div>
          
          <div className="p-4 space-y-3">
            {/* Category and date */}
            <div className="flex gap-3">
              <div className="h-5 bg-gray-200 rounded w-16" />
              <div className="h-5 bg-gray-200 rounded w-24" />
            </div>
            
            {/* Title */}
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-full" />
              <div className="h-6 bg-gray-200 rounded w-4/5" />
            </div>
            
            {/* Views */}
            <div className="h-4 bg-gray-200 rounded w-20" />
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Category Block Skeleton
// ============================================================================

const CategoryBlockSkeleton: React.FC<{ columns: number }> = ({ columns }) => {
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-4`}>
      {Array.from({ length: columns * 2 }).map((_, i) => (
        <div key={i} className="animate-pulse bg-white rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gray-200 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="h-5 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Ad Block Skeleton
// ============================================================================

const AdBlockSkeleton: React.FC = () => {
  return (
    <div className="animate-pulse">
      <div className="bg-gray-200 rounded-lg flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-300 rounded-full mx-auto mb-3" />
          <div className="h-4 bg-gray-300 rounded w-32 mx-auto" />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Profile Block Skeleton
// ============================================================================

const ProfileBlockSkeleton: React.FC<{ columns: number }> = ({ columns }) => {
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-6`}>
      {Array.from({ length: columns * 2 }).map((_, i) => (
        <div key={i} className="animate-pulse bg-white rounded-lg p-6 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <div className="w-24 h-24 bg-gray-200 rounded-full mb-4" />
            <div className="h-6 bg-gray-200 rounded w-32 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
            <div className="space-y-2 w-full">
              <div className="h-3 bg-gray-200 rounded w-full" />
              <div className="h-3 bg-gray-200 rounded w-5/6 mx-auto" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Gallery Block Skeleton
// ============================================================================

const GalleryBlockSkeleton: React.FC<{ columns: number }> = ({ columns }) => {
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-4`}>
      {Array.from({ length: columns * 3 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-square bg-gray-200 rounded-lg" />
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Text Block Skeleton
// ============================================================================

const TextBlockSkeleton: React.FC = () => {
  return (
    <div className="animate-pulse bg-white rounded-lg p-6 shadow-sm">
      <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-5/6" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-4/5" />
      </div>
    </div>
  );
};

// ============================================================================
// Images Block Skeleton
// ============================================================================

const ImagesBlockSkeleton: React.FC<{ columns: number }> = ({ columns }) => {
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-4`}>
      {Array.from({ length: columns * 2 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-[4/3] bg-gray-200 rounded-lg" />
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Item Detail Block Skeleton
// ============================================================================

const ItemDetailBlockSkeleton: React.FC = () => {
  return (
    <div className="animate-pulse bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="h-96 bg-gray-200" />
      <div className="p-6 space-y-4">
        <div className="h-10 bg-gray-200 rounded w-2/3" />
        <div className="flex gap-4">
          <div className="h-4 bg-gray-200 rounded w-24" />
          <div className="h-4 bg-gray-200 rounded w-32" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded w-full" />
          ))}
          <div className="h-4 bg-gray-200 rounded w-4/5" />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Generic Block Skeleton
// ============================================================================

const GenericBlockSkeleton: React.FC<{ columns: number }> = ({ columns }) => {
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-6`}>
      {Array.from({ length: columns }).map((_, i) => (
        <div key={i} className="animate-pulse bg-white rounded-lg p-6 shadow-sm">
          <div className="h-32 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContentBlockSkeleton;
